import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {ColumnDef} from "@tanstack/react-table";
import CommonConfirmDialog from "crqlar-react-common/dist/components/dialog/CommonConfirmDialog";
import {AgeCategoryType} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {
    ArrivalsStayPeriod,
    type RestaurantArrivalsLinkedReservationDTO,
    type RestaurantArrivalsReservationListRequestDTO,
    type RestaurantArrivalsReservationListRowDTO,
} from "crqlar-react-common/dist/models/aggregation/reservationListAggregation.model";
import {
    type Reservation,
    ReservationStatus,
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {ReservationAggregationService} from "crqlar-react-common/dist/services/aggregation/reservationAggregation.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {
    addMinutes,
    createReservationStartDateBasedOnPeriod,
    endOfDay,
    formatFullDMY,
    startOfDay,
} from "crqlar-react-common/dist/utils/date.util";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useService} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {enqueueSnackbar} from "notistack";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import {RightSidebarFloatingPanel} from "../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../layout/rightSidebar/RightSidebarPanelHeader";
import {StayStatusFilterIcon} from "../../layout/rightSidebar/StayStatusFilterIcon";
import {
    type RadioFilterOption,
    RadioFilterPanel,
} from "../../layout/rightSidebar/RadioFilterPanel";
import {
    RightSidebarDivider,
    RightSidebarPanelButton,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import RangeDatePicker from "../../common/newDatePicker/RangeDatePicker";
import {useDebouncedDateRange} from "../../common/newDatePicker/datePickerShared";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailExport,
    ServerTableRailFullscreen,
    ServerTableRailPrint,
    ServerTableRailSearch,
    ServerTableRailSort,
    serverTableExportLimit,
    type ServerTableExportQuery,
    useServerTable,
} from "../../common/serverTable";
import {AgeCategoriesElement} from "../../common/uiElements/AgeCategoriesElement";
import {AliasAgeCategoriesElement} from "../../common/uiElements/AliasAgeCategoriesElement";
import {CountryElement} from "../../common/uiElements/CountryElement";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import {NationalityElement} from "../../common/uiElements/NationalityElement";
import {NotesElement} from "../../common/uiElements/NotesElement";
import {noteTexts} from "crqlar-react-common/dist/utils/noteTypeGroup.util";
import {ReservationStatusElement} from "../../common/uiElements/ReservationStatusElement";
import {resolveGuestCountsFromTypeTotals} from "../../common/ageCategoryDefaults";
import {
    type ReservationElementItem,
    ReservationsElement,
} from "../../common/uiElements/ReservationsElement";
import {AccommodationElement, AccommodationReferences} from "../../common/uiElements/ResourceElement";
import {getValidDateTimeValue} from "../../common/uiElements/utils";
import ReservationDayPeriodDialog from "../ReservationDayPeriodDialog";
import ReservationDialog from "../reservationDialog/ReservationDialog";

type ReservationPeriod = "breakfast" | "lunch" | "dinner" | "blank";
type ReservationPeriodSelection = ReservationPeriod | "breakfast-plus-dinner";

const guestName = (guest: RestaurantArrivalsReservationListRowDTO["primaryGuest"]) =>
    formatProfileListName(guest) || guest?.nameOrdered?.trim();

const primaryGuestName = (row: RestaurantArrivalsReservationListRowDTO) =>
    guestName(row.primaryGuest) || row.primaryGuestName?.trim();

const guestNames = (row: RestaurantArrivalsReservationListRowDTO) => {
    const additionalNames = row.additionalGuests?.length
        ? row.additionalGuests.map(guestName)
        : row.guestNames ?? [];

    return [primaryGuestName(row), ...additionalNames]
        .map(name => name?.trim())
        .filter((name): name is string => Boolean(name));
};

/*
    The row's room:

    - guest in the house: `currentStayRoom` — the current-stay snapshot scoped to this row's stay,
      the exact value ReservationProfile renders in the reservation dialog;
    - otherwise: `bookedRooms` — the room(s) of this row's own stay, whether the guest has not
      arrived yet or has already left. On a past date that is the room the guest had on that stay.
*/
const arrivalRoomNames = (row: RestaurantArrivalsReservationListRowDTO): string[] => {
    const currentStayRoom = row.currentStayRoom?.trim();
    if (currentStayRoom) return [currentStayRoom];

    return row.bookedRooms
        ?.map(room => room.trim())
        .filter(Boolean) ?? [];
};

const ArrivalRooms = ({row}: {row: RestaurantArrivalsReservationListRowDTO}) => {
    const currentStayRoom = row.currentStayRoom?.trim();
    if (currentStayRoom) return <AccommodationElement name={currentStayRoom}/>;

    const bookedRooms = arrivalRoomNames(row);
    if (!bookedRooms.length) return null;

    // Stay rooms come from the live room resources when they resolve — render those chips so they
    // keep their tag colour; otherwise they are the plain-text stay snapshot.
    const bookedResources = row.accommodationResources
        ?.filter(resource => resource.name && bookedRooms.includes(resource.name.trim()));
    if (bookedResources?.length) return <AccommodationReferences resources={bookedResources}/>;

    return (
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
            {bookedRooms.map(room => <AccommodationElement key={room} name={room}/>)}
        </Box>
    );
};

const arrivalNotes = (row: RestaurantArrivalsReservationListRowDTO) => [
    ...(row.profileNotes ?? []),
    ...(row.notes ?? []),
];

const formatArrivalStay = (
    row: RestaurantArrivalsReservationListRowDTO,
    statusLabel?: string,
) => {
    const start = getValidDateTimeValue(row.start);
    const end = getValidDateTimeValue(row.end);
    if (!start || !end) return "-";

    const dateRange = `${formatFullDMY(start)} - ${formatFullDMY(end)}`;
    return statusLabel ? `${dateRange} (${statusLabel})` : dateRange;
};

const reservationItems = (
    reservations: RestaurantArrivalsLinkedReservationDTO[] | undefined,
): ReservationElementItem[] => (reservations ?? []).map(reservation => ({
    reservationId: reservation.id,
    status: reservation.status,
    start: reservation.start,
    end: reservation.end,
    serviceName: reservation.serviceName,
    restaurants: reservation.restaurants,
    tables: reservation.tables,
    guestCount: reservation.guestCount,
    eatPeriod: reservation.eatPeriod,
}));

/*
    Guests the stay counts but cannot name. The PMS sends the headcount at booking and the
    accompanying guests' names only at registration, so a two-person booking legitimately arrives
    with one name and fills in at check-in. Without this the Guests cell reads as a one-person
    booking while the Count column next to it says 2, which is what the PMS/POS shows too.
*/
const unnamedGuestCount = (row: RestaurantArrivalsReservationListRowDTO) =>
    Math.max(0, (row.guestCount ?? 0) - guestNames(row).length);

const guestNamesWithUnnamed = (row: RestaurantArrivalsReservationListRowDTO, unnamedLabel: string) => {
    const unnamed = unnamedGuestCount(row);
    return [...guestNames(row), ...(unnamed > 0 ? [unnamedLabel] : [])];
};

const GuestNamesElement = ({row}: {row: RestaurantArrivalsReservationListRowDTO}) => {
    const {t} = useTranslation();
    const names = guestNames(row);
    const unnamed = unnamedGuestCount(row);
    if (!names.length && !unnamed) return null;
    const hasPrimaryGuest = Boolean(primaryGuestName(row));

    return (
        <Stack gap={0.25} sx={{whiteSpace: "normal"}}>
            {names.map((name, index) => (
                <Typography
                    key={`${name}-${index}`}
                    variant="body1"
                    sx={{fontWeight: hasPrimaryGuest && index === 0 ? 600 : 400, lineHeight: 1.35}}
                >
                    {name}
                </Typography>
            ))}
            {unnamed > 0 && (
                <Typography variant="body2" sx={{color: "text.secondary", lineHeight: 1.35}}>
                    {t("+{{count}} unnamed", {count: unnamed})}
                </Typography>
            )}
        </Stack>
    );
};

const ArrivalStayPeriodFilter = ({
    value,
    onChange,
}: {
    value: ArrivalsStayPeriod;
    onChange: (value: ArrivalsStayPeriod) => void;
}) => {
    const {t} = useTranslation();
    const options = useMemo<readonly RadioFilterOption<ArrivalsStayPeriod>[]>(() => [
        {value: ArrivalsStayPeriod.CHECKING_IN, label: t("Checking in")},
        {value: ArrivalsStayPeriod.STAYING, label: t("In-House")},
        {value: ArrivalsStayPeriod.CHECKING_OUT, label: t("Checking out")},
    ], [t]);

    return (
        <RightSidebarPanelButton
            icon={(
                <StayStatusFilterIcon
                    active={value !== ArrivalsStayPeriod.CHECKING_IN}
                />
            )}
            label={t("Stay period")}
            renderPanel={close => (
                <RadioFilterPanel<ArrivalsStayPeriod>
                    title={t("Stay period")}
                    options={options}
                    value={value}
                    onChange={nextValue => {
                        onChange(nextValue);
                        close();
                    }}
                    onReset={value === ArrivalsStayPeriod.CHECKING_IN
                        ? undefined
                        : () => {
                            onChange(ArrivalsStayPeriod.CHECKING_IN);
                            close();
                        }}
                    onClose={close}
                />
            )}
        />
    );
};

const ArrivalsInformationButton = () => {
    const {t} = useTranslation();

    return (
        <RightSidebarPanelButton
            icon={<InfoOutlinedIcon/>}
            label={t("Information")}
            renderPanel={close => (
                <RightSidebarFloatingPanel>
                    <RightSidebarPanelHeader title={t("Information")} onClose={close}/>
                    <Typography
                        variant="body1"
                        sx={{px: 2, pb: 2, whiteSpace: "pre-line"}}
                    >
                        {t("arrivals_mismatch_info")}
                    </Typography>
                </RightSidebarFloatingPanel>
            )}
        />
    );
};

export default function RestaurantArrivalsList({sector}: {sector: Sector}) {
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const defaultService = useService(sector.defaultServiceId);
    const ageCategories = useAllAgeCategories();
    const sectorId = sector.id!;
    const {range: dateRange, queryRange, onRangeChange} = useDebouncedDateRange(sectorId);
    const [stayPeriod, setStayPeriod] = useState(ArrivalsStayPeriod.CHECKING_IN);
    const [selectedReservation, setSelectedReservation] = useState<Reservation>();
    const [creationRow, setCreationRow] = useState<RestaurantArrivalsReservationListRowDTO>();
    const [periodDialogOpen, setPeriodDialogOpen] = useState(false);
    const [linkedReservationDrafts, setLinkedReservationDrafts] = useState<Reservation[]>();
    const [pendingCancelRow, setPendingCancelRow] = useState<RestaurantArrivalsReservationListRowDTO>();

    const handleOpenLinkedReservation = useCallback(async (item: ReservationElementItem) => {
        if (!item.reservationId) return;

        try {
            const reservation = await ReservationService.getReservationById(item.reservationId);
            if (!reservation) throw new Error("Reservation not found");
            setCreationRow(undefined);
            setLinkedReservationDrafts(undefined);
            setSelectedReservation(reservation);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to open reservation"), {variant: "error"});
        }
    }, [t]);

    const handleAddReservation = useCallback((row: RestaurantArrivalsReservationListRowDTO) => {
        setCreationRow(row);
        setPeriodDialogOpen(true);
    }, []);

    // Sorting, search and print see the same "+1 unnamed" line the cell renders.
    const unnamedLabel = useCallback(
        (row: RestaurantArrivalsReservationListRowDTO) =>
            t("+{{count}} unnamed", {count: unnamedGuestCount(row)}),
        [t],
    );

    const createReservationDraft = useCallback((
        row: RestaurantArrivalsReservationListRowDTO,
        period: ReservationPeriod,
    ): Reservation => {
        const start = createReservationStartDateBasedOnPeriod(row.start ?? new Date(), period);
        const guestCounts = resolveGuestCountsFromTypeTotals(
            ageCategories,
            {
                [AgeCategoryType.ADULT]: row.guestCounts?.adults ?? 0,
                [AgeCategoryType.TEENAGER]: row.guestCounts?.teenagers ?? 0,
                [AgeCategoryType.CHILD]: row.guestCounts?.children ?? 0,
                [AgeCategoryType.INFANT]: row.guestCounts?.infants ?? 0,
            },
            (row.aliasAgeCategories ?? []).map(ageCategory => ({
                ageCategoryId: ageCategory.id,
                count: ageCategory.count,
                type: ageCategory.type,
            })),
        );

        const profileIds = row.profileIds?.length
            ? row.profileIds
            : row.profileId ? [row.profileId] : [];

        return {
            expectedStartDate: start,
            expectedEndDate: addMinutes(start, defaultService?.durationMinutes ?? 60),
            profileIds,
            primaryProfileId: row.profileId,
            guestCounts,
            sectorId,
            status: ReservationStatus.CONFIRMED,
            serviceId: defaultService?.id ?? sector.defaultServiceId,
            meta: {eatPeriod: period},
        };
    }, [ageCategories, defaultService, sector.defaultServiceId, sectorId]);

    const handlePeriodConfirm = useCallback((period: ReservationPeriodSelection) => {
        if (!creationRow) return;

        const drafts = period === "breakfast-plus-dinner"
            ? [
                createReservationDraft(creationRow, "breakfast"),
                createReservationDraft(creationRow, "dinner"),
            ]
            : [createReservationDraft(creationRow, period)];

        setPeriodDialogOpen(false);
        setLinkedReservationDrafts(drafts.length > 1 ? drafts : undefined);
        setSelectedReservation(drafts[0]);
    }, [createReservationDraft, creationRow]);

    const columns = useMemo<ColumnDef<RestaurantArrivalsReservationListRowDTO>[]>(() => [
        {
            id: "profile",
            header: t("Guests"),
            accessorFn: row => guestNamesWithUnnamed(row, unnamedLabel(row)).join(", "),
            cell: ({row}) => <GuestNamesElement row={row.original}/>,
            size: 240,
            meta: {
                defaultActive: true,
                defaultPinned: true,
                isServerSortable: true,
                serverSortField: "lastName",
                printable: true,
                printDefault: true,
                printCell: row => guestNamesWithUnnamed(row.original, unnamedLabel(row.original)).join(", "),
            },
        },
        {
            id: "countryCode",
            header: t("Country"),
            accessorFn: row => row.primaryGuest?.countryCode,
            cell: ({row}) => <CountryElement countryCode={row.original.primaryGuest?.countryCode}/>,
            size: 130,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <CountryElement countryCode={row.original.primaryGuest?.countryCode} asPrint/>
                ),
            },
        },
        {
            id: "nationality",
            header: t("Nationality"),
            accessorFn: row => row.primaryGuest?.nationality,
            cell: ({row}) => (
                <NationalityElement nationality={row.original.primaryGuest?.nationality}/>
            ),
            size: 130,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => (
                    <NationalityElement nationality={row.original.primaryGuest?.nationality} asPrint/>
                ),
            },
        },
        {
            id: "languageCodes",
            header: t("Languages"),
            accessorFn: row => row.primaryGuest?.languageCodes ?? [],
            cell: ({row}) => (
                <LanguagesElement languageCodes={row.original.primaryGuest?.languageCodes}/>
            ),
            size: 150,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => (
                    <LanguagesElement languageCodes={row.original.primaryGuest?.languageCodes} asPrint/>
                ),
            },
        },
        {
            id: "stay",
            header: t("Check in - out"),
            accessorFn: row => row.start,
            cell: ({row}) => {
                const statusLabel = row.original.status
                    ? t(`Reservation status ${row.original.status}`)
                    : undefined;
                const label = formatArrivalStay(row.original, statusLabel);
                if (label === "-") return <Box>-</Box>;
                const trailingTokenStart = label.lastIndexOf(" ") + 1;

                return (
                    <MuiLink
                        component={RouterLink}
                        to={`/system/reservations/${row.original.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                        underline="hover"
                        onClick={event => event.stopPropagation()}
                        sx={{lineHeight: 1.5}}
                    >
                        {label.slice(0, trailingTokenStart)}
                        <Box
                            component="span"
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                whiteSpace: "nowrap",
                                textDecoration: "inherit",
                            }}
                        >
                            {label.slice(trailingTokenStart)}
                            <OpenInNewIcon
                                color="primary"
                                sx={{fontSize: 16, ml: 0.5, flex: "0 0 auto"}}
                            />
                        </Box>
                    </MuiLink>
                );
            },
            size: 320,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "start",
                printable: true,
                printDefault: true,
                printCell: row => formatArrivalStay(
                    row.original,
                    row.original.status
                        ? t(`Reservation status ${row.original.status}`)
                        : undefined,
                ),
            },
        },
        {
            id: "lastReservationNote",
            header: t("Last Reservation Note"),
            accessorFn: row => noteTexts(arrivalNotes(row)),
            cell: ({row}) => {
                const hasReservationNotes = noteTexts(row.original.notes).length > 0;
                const profileId = row.original.profileId;
                const detailsHref = hasReservationNotes
                    ? `/system/reservations/${row.original.id}`
                    : profileId ? `/system/profiles/${profileId}` : undefined;

                return (
                    <NotesElement
                        notes={arrivalNotes(row.original)}
                        detailsHref={detailsHref}
                        detailsLabel={hasReservationNotes ? t("Open reservation") : t("Profile")}
                    />
                );
            },
            size: 280,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => <NotesElement notes={arrivalNotes(row.original)} asPrint/>,
            },
        },
        {
            id: "service",
            header: t("Room code"),
            accessorFn: row => row.mealCode ?? row.serviceName,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 160,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "serviceName",
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "resources",
            header: t("Room"),
            accessorFn: arrivalRoomNames,
            cell: ({row}) => <ArrivalRooms row={row.original}/>,
            size: 170,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "accommodationResources",
                printable: true,
                printDefault: true,
                printCell: row => arrivalRoomNames(row.original).join(", ") || null,
            },
        },
        {
            id: "guestCount",
            header: t("Guest count abbreviation"),
            accessorFn: row => row.guestCount,
            cell: ({getValue}) => <Box>{getValue<number | undefined>() ?? null}</Box>,
            size: 120,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "guestCounts",
            header: t("Age categories"),
            accessorFn: row => (
                (row.guestCounts?.adults ?? 0)
                + (row.guestCounts?.teenagers ?? 0)
                + (row.guestCounts?.children ?? 0)
                + (row.guestCounts?.infants ?? 0)
            ),
            cell: ({row}) => (
                <AgeCategoriesElement
                    ageCategories={ageCategories}
                    referenceDate={queryRange.start}
                    computedAgeCategories={row.original.guestCounts}
                />
            ),
            size: 160,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "guestCounts",
                printable: true,
                printDefault: true,
                printCell: row => (
                    <AgeCategoriesElement
                        ageCategories={ageCategories}
                        referenceDate={queryRange.start}
                        computedAgeCategories={row.original.guestCounts}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "aliasAgeCategories",
            header: `${t("Age categories")} (${t("Alias")})`,
            accessorFn: row => row.aliasAgeCategories,
            cell: ({row}) => (
                <AliasAgeCategoriesElement
                    aliasAgeCategories={row.original.aliasAgeCategories}
                    ageCategories={ageCategories}
                />
            ),
            size: 160,
            meta: {
                defaultActive: false,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <AliasAgeCategoriesElement
                        aliasAgeCategories={row.original.aliasAgeCategories}
                        ageCategories={ageCategories}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "reservations",
            header: t("Reservations"),
            accessorFn: row => row.linkedReservations ?? [],
            cell: ({row}) => {
                const linkedReservations = reservationItems(row.original.linkedReservations);
                const canCancel = row.original.linkedReservations?.some(
                    reservation => reservation.status !== ReservationStatus.CANCELLED,
                );

                return (
                    <ReservationsElement
                        reservations={linkedReservations}
                        now={new Date()}
                        onSelect={handleOpenLinkedReservation}
                        onAdd={readOnly ? undefined : () => handleAddReservation(row.original)}
                        onCancelAll={readOnly || !canCancel
                            ? undefined
                            : () => setPendingCancelRow(row.original)}
                        showDate
                    />
                );
            },
            size: 360,
            meta: {
                defaultActive: true,
                grow: true,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <ReservationsElement
                        reservations={reservationItems(row.original.linkedReservations)}
                        now={new Date()}
                        asPrint
                        showDate
                    />
                ),
                printOriginalCell: row => (
                    <ReservationsElement
                        reservations={reservationItems(row.original.linkedReservations)}
                        now={new Date()}
                        asPrint
                        printVariant="original"
                        showDate
                    />
                ),
            },
        },
        {
            id: "status",
            header: t("Status"),
            accessorFn: row => row.status,
            cell: ({row}) => (
                <ReservationStatusElement
                    status={row.original.status}
                    start={row.original.start}
                    end={row.original.end}
                />
            ),
            size: 160,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                printable: true,
                printCell: row => <ReservationStatusElement status={row.original.status} asPrint/>,
            },
        },
        {
            id: "source",
            header: t("Source"),
            accessorFn: row => row.fromBookingEngine ? t("Online Reservation") : t("Staff"),
            cell: ({getValue}) => getValue<string>(),
            size: 150,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                serverSortField: "fromBookingEngine",
                printable: true,
            },
        },
        {
            id: "code",
            header: t("Reservation code"),
            accessorFn: row => row.code,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 170,
            meta: {defaultActive: false, isServerSortable: true, printable: true},
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: row => row.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true},
        },
    ], [
        ageCategories,
        queryRange.start,
        handleAddReservation,
        handleOpenLinkedReservation,
        readOnly,
        sector.name,
        t,
        unnamedLabel,
    ]);

    const controller = useServerTable<RestaurantArrivalsReservationListRowDTO>({
        tableId: `restaurant-arrivals-list-${sectorId}`,
        columns,
        getRowId: row => row.id,
        initialSorting: [{id: "stay", desc: false}],
        queryDependencies: [
            sectorId,
            queryRange.start.getTime(),
            queryRange.end.getTime(),
            stayPeriod,
        ],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await ReservationAggregationService.listRestaurantArrivals({
                sectorId,
                search: searchQuery || undefined,
                stayPeriod,
                range: {
                    start: startOfDay(queryRange.start),
                    end: endOfDay(queryRange.end),
                },
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content,
                totalElements: page.totalElements,
            };
        },
    });

    // The export's request: the list's own, without paging.
    const exportRequest = useCallback((
        {searchQuery, sorting}: ServerTableExportQuery,
    ): RestaurantArrivalsReservationListRequestDTO => ({
        sectorId,
        search: searchQuery || undefined,
        stayPeriod,
        range: {
            start: startOfDay(queryRange.start),
            end: endOfDay(queryRange.end),
        },
        limit: serverTableExportLimit(sorting),
    }), [queryRange.end, queryRange.start, sectorId, stayPeriod]);

    const closeReservationDialog = () => {
        setSelectedReservation(undefined);
        setCreationRow(undefined);
        setLinkedReservationDrafts(undefined);
    };

    const handleReservationSaved = () => {
        closeReservationDialog();
        void controller.query.refetch();
    };

    const handleConfirmCancelAll = async () => {
        const row = pendingCancelRow;
        setPendingCancelRow(undefined);
        if (!row) return;

        try {
            const reservations = (await Promise.all(
                (row.linkedReservations ?? [])
                    .filter(reservation => reservation.status !== ReservationStatus.CANCELLED)
                    .map(reservation => ReservationService.getReservationById(reservation.id)),
            )).filter((reservation): reservation is Reservation => Boolean(reservation));

            if (reservations.length) {
                await ReservationService.updateMulti(reservations.map(reservation => ({
                    ...reservation,
                    status: ReservationStatus.CANCELLED,
                })));
                enqueueSnackbar(t("Successfully cancelled reservations"), {variant: "success"});
                void controller.query.refetch();
            }
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to cancel reservations"), {variant: "error"});
        }
    };

    return (
        <>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <RightSidebarRailLayout>
                        <RightSidebarRailHeader>
                            <RightSidebarRailHeaderCenter>
                                <RangeDatePicker
                                    value={dateRange}
                                    onChange={onRangeChange}
                                    showStepButtons
                                    size="medium"
                                />
                            </RightSidebarRailHeaderCenter>
                        </RightSidebarRailHeader>

                        <ServerTable/>

                        <RightSidebarRail>
                            <ServerTableRailSearch/>
                            <ServerTableRailSort/>
                            <ArrivalStayPeriodFilter value={stayPeriod} onChange={setStayPeriod}/>
                            <ServerTableRailFullscreen/>
                            <ServerTableRailColumns/>
                            <RightSidebarDivider/>
                            <ServerTableRailPrint title={t("Arrivals")}/>
                            <ServerTableRailExport
                                list="restaurant-arrivals"
                                title={t("Arrivals")}
                                buildRequest={exportRequest}
                            />
                            <ArrivalsInformationButton/>
                        </RightSidebarRail>
                    </RightSidebarRailLayout>
                </ServerTableFrame>
            </ServerTableProvider>

            <ReservationDayPeriodDialog
                open={periodDialogOpen}
                onClose={() => {
                    setPeriodDialogOpen(false);
                    setCreationRow(undefined);
                }}
                onConfirm={handlePeriodConfirm}
            />

            <ReservationDialog
                open={Boolean(selectedReservation)}
                reservation={selectedReservation}
                onClose={closeReservationDialog}
                onSave={handleReservationSaved}
                onSaveBlock={handleReservationSaved}
                onChangeReservation={setSelectedReservation}
                linkedReservationsParam={linkedReservationDrafts}
                customSort={[{field: "expectedStartDate", direction: "ASC"}]}
                defaultMultidayUntil={!selectedReservation?.id ? creationRow?.end : undefined}
                defaultAllowNotify={false}
            />

            <CommonConfirmDialog
                open={Boolean(pendingCancelRow)}
                onClose={() => setPendingCancelRow(undefined)}
                onConfirm={() => void handleConfirmCancelAll()}
            />
        </>
    );
}
